import { ArrowLeftIcon, SaveIcon, TrashIcon } from 'lucide-react';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  EXAMPLE_GROUP_ID,
  queryKeys,
  useFormWithValidation,
} from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import {
  CreateGroupDto,
  GroupCategory,
  GroupMemberPermissionsDto,
  GroupResponseDto,
  SplitMethod,
  UpdateGroupDto,
  UploadUrlResponseDto,
  effectivePermissionsForRole,
  getCurrencyDecimals,
  hasAnyEditPermission,
  resolveMemberPermissions,
} from '@splitfiesta/shared';
import { Alert, Button, FormButton } from '@splitfiesta/ui';

import { withAuth } from '@/components/auth';
import { FormError } from '@/components/common/EnhancedErrorDisplay';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
// Import all form components statically (bundled together for better performance)
import { GroupBasicInfo } from '@/components/groups/GroupBasicInfo';
import { GroupDefaultSplitSettings } from '@/components/groups/GroupDefaultSplitSettings';
import { GroupMemberSelection } from '@/components/groups/GroupMemberSelection';
import { GroupMoneySettings } from '@/components/groups/GroupMoneySettings';
// DISABLED: Backend not implemented yet. See docs/PAYMENT_REMINDERS_PLAN.md
// import { GroupPaymentReminders } from '@/components/groups/GroupPaymentReminders';
import { GroupMemberPermissionsSection } from '@/components/groups/sections/GroupMemberPermissionsSection';
import { GroupProSettingsSection } from '@/components/groups/sections/GroupProSettingsSection';
import { GroupRecurringSection } from '@/components/groups/sections/GroupRecurringSection';
import { AppLayout } from '@/components/layout/AppLayout';
import { GroupFormSkeleton } from '@/components/skeleton';

import { useGroupExpensesInfiniteQuery } from '@/hooks/queries/useExpenses';
import {
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useGroupQuery,
  useUpdateGroupMutation,
} from '@/hooks/queries/useGroups';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useRouter } from '@/hooks/useRouteChangeRouter';
import { useTypedApi } from '@/hooks/useTypedApi';

import { useAuthStore } from '@/stores/authStore';

import { optimizeImageForUpload } from '@/utils/imageOptimization';
import { toast } from '@/utils/toast';

// Extended interface for edit mode - includes additional fields not in base DTO
interface ExtendedGroupResponse extends GroupResponseDto {
  budgetAmount?: number;
  categorySettings?: {
    startDate?: string;
    endDate?: string;
    settlementReminder?: {
      enabled: boolean;
      frequency: 'weekly' | 'biweekly' | 'monthly';
    };
  };
  simplifyDebts?: boolean;
  defaultSplitConfig?: {
    percentages?: Record<string, number>;
    shares?: Record<string, number>;
  };
}

interface GroupFormProps {
  groupId?: string;
  mode?: 'create' | 'edit';
}

function GroupForm({ groupId, mode = 'create' }: GroupFormProps) {
  const [group, setGroup] = useState<ExtendedGroupResponse | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<GroupCategory>('other');
  const [currency, setCurrency] = useState('');
  const [defaultSplitMethod, setDefaultSplitMethod] = useState<
    'equal' | 'percentage' | 'shares'
  >('equal');
  const [defaultSplitConfig, setDefaultSplitConfigState] = useState<{
    percentages?: Record<string, number>;
    shares?: Record<string, number>;
  }>({});
  // Ref to track latest split config for immediate access in save handler (avoids effect timing issues)
  const splitConfigRef = useRef<{
    percentages?: Record<string, number>;
    shares?: Record<string, number>;
  }>({});
  // Wrapper that updates both state and ref to keep them in sync
  const setDefaultSplitConfig = useCallback(
    (config: {
      percentages?: Record<string, number>;
      shares?: Record<string, number>;
    }) => {
      splitConfigRef.current = config;
      setDefaultSplitConfigState(config);
    },
    []
  );
  const [budget, setBudget] = useState<string | undefined>(''); // For CurrencyInput
  const [numericBudget, setNumericBudget] = useState<number>(0); // For calculations
  // Category-specific settings (reuse startDate/endDate for all categories)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // DISABLED: Backend not implemented yet. See docs/PAYMENT_REMINDERS_PLAN.md
  // const [settlementReminderEnabled, setSettlementReminderEnabled] =
  //   useState(false);
  // const [settlementReminderFrequency, setSettlementReminderFrequency] =
  //   useState<'weekly' | 'biweekly' | 'monthly'>('monthly');
  // Member selection state
  const [expenseCount, setExpenseCount] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  // Track if form has been initialized from backend data (prevent re-init on refetch)
  const [hasInitialized, setHasInitialized] = useState(false);
  // Image file for deferred upload (create mode)
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  // Vertical focal point for the group photo (0=top, 0.5=center, 1=bottom)
  const [imageFocalY, setImageFocalY] = useState(0.5);
  // Member permissions policy — owner-only, persisted in edit mode
  const [memberPermissions, setMemberPermissions] =
    useState<GroupMemberPermissionsDto>(resolveMemberPermissions());

  const user = useAuthStore(state => state.user);
  const { t, ready } = useTranslation();
  const router = useRouter();
  const { handleError } = useErrorHandler('groups');
  const api = useTypedApi();
  const queryClient = useQueryClient();

  // Use React Query for data fetching
  // Disable query when deleting to prevent refetch attempts during navigation
  const { data: fetchedGroup, isLoading: groupLoading } = useGroupQuery(
    groupId || '',
    mode === 'edit' && !!groupId && !isDeleting
  );
  const { data: expensesData } = useGroupExpensesInfiniteQuery(
    groupId || '',
    { limit: 1 },
    mode === 'edit' && !!groupId && !isDeleting
  );

  // Mutations
  const createGroupMutation = useCreateGroupMutation();
  const updateGroupMutation = useUpdateGroupMutation(groupId || '');
  const deleteGroupMutation = useDeleteGroupMutation();

  // Load required namespaces
  const groupsLoaded = useNamespace('groups');
  const commonLoaded = useNamespace('common');
  const currenciesLoaded = useNamespace('currencies');

  // Wrapper for setCategory that clears incompatible dates when category changes
  const handleCategoryChange = useCallback((newCategory: GroupCategory) => {
    setCategory(newCategory);

    // Clear dates based on new category's requirements
    // Categories that use dates: trip, events, roommates, work
    // Categories that don't use dates: friends, family, couple, other
    const categoriesWithDates = ['trip', 'events', 'roommates', 'work'];
    const categoriesWithEndDate = ['trip', 'roommates', 'work']; // events only uses startDate

    if (!categoriesWithDates.includes(newCategory)) {
      // Clear all dates for categories that don't use them
      setStartDate('');
      setEndDate('');
    } else if (!categoriesWithEndDate.includes(newCategory)) {
      // Clear only endDate for events (only uses startDate)
      setEndDate('');
    }
  }, []);

  // Wrapper for setDefaultSplitMethod - only updates the method
  // The child component (GroupDefaultSplitSettings) manages its own local state for
  // percentages/shares and syncs back via setDefaultSplitConfig. We must NOT clear
  // splitConfigRef here because the child's sync effect uses lastSyncedRef to avoid
  // infinite loops — if we clear the ref but effective values haven't changed, the
  // sync effect skips and the ref stays empty, causing data loss on save.
  // The save handler already extracts only the active method's config, so clearing is unnecessary.
  const handleSplitMethodChange = useCallback(
    (method: 'equal' | 'percentage' | 'shares') => {
      setDefaultSplitMethod(method);
    },
    []
  );

  // Form validation and submission
  const {
    handleSubmit: handleFormSubmit,
    isSubmitting: isLoading,
    error,
    setError,
  } = useFormWithValidation({
    onSubmit: async () => {
      if (mode === 'create') {
        // Handle budget: distinguish between empty (undefined) and zero (0)
        // If budget string is empty/undefined, don't send budgetAmount at all
        // If budget string has a value (including "0"), send the numeric value
        const budgetValue = budget?.trim() ? numericBudget : undefined; // Only send budgetAmount if user actually entered a value

        // Build categorySettings only if at least one date is set
        const categorySettingsPayload =
          startDate || endDate
            ? {
                startDate: startDate || undefined,
                endDate: endDate || undefined,
              }
            : undefined;

        // Build defaultSplitConfig based on method
        // Normalize keys to use the authenticated user ID (user.id) since the
        // child component may have captured a stale ID from Zustand hydration
        const currentSplitConfig = splitConfigRef.current;
        let splitConfig:
          | {
              percentages?: Record<string, number>;
              shares?: Record<string, number>;
            }
          | undefined = undefined;
        if (defaultSplitMethod === 'percentage') {
          splitConfig = { percentages: { [user!.id]: 100 } };
        } else if (defaultSplitMethod === 'shares') {
          // Preserve the shares value the user set, but assign to correct userId
          const sharesValue = currentSplitConfig?.shares
            ? (Object.values(currentSplitConfig.shares)[0] ?? 1)
            : 1;
          splitConfig = { shares: { [user!.id]: Math.max(1, sharesValue) } };
        }

        const createPayload: CreateGroupDto & {
          simplifyDebts?: boolean;
          memberFriendIds?: string[];
          memberEmails?: string[];
        } = {
          name: name.trim(),
          description: description.trim() || undefined,
          category,
          currency: currency || 'USD',
          imageFocalY,
          budgetAmount: budgetValue,
          categorySettings: categorySettingsPayload,
          // Additional fields not in DTO but accepted by API
          simplifyDebts: true,
          defaultSplitMethod,
          defaultSplitConfig: splitConfig,
          // The creator is the owner-to-be and may set the initial policy.
          memberPermissions,
        };
        const newGroup = await createGroupMutation.mutateAsync(createPayload);
        const newGroupId = newGroup.id;

        // Upload pending image if one was selected (dual-upload: optimized + original)
        if (pendingImageFile) {
          try {
            // Optimize image (resize for display, keep original for backup)
            const { optimized, original } = await optimizeImageForUpload(
              pendingImageFile,
              'group'
            );

            // Get file extensions
            const optimizedExt =
              optimized.name.split('.').pop()?.toLowerCase() || 'webp';
            const originalExt =
              original.name.split('.').pop()?.toLowerCase() || '';

            // Get presigned URLs for both versions in parallel
            const [optimizedResponse, originalResponse] = await Promise.all([
              api.upload.getPresignedUrl({
                type: 'group',
                entityId: newGroupId,
                fileExtension: optimizedExt,
              }),
              api.upload.getPresignedUrl({
                type: 'group',
                entityId: newGroupId,
                fileExtension: originalExt,
                variant: 'original',
              }),
            ]);

            const optimizedData = (
              optimizedResponse as unknown as { data: UploadUrlResponseDto }
            ).data;
            const originalData = (
              originalResponse as unknown as { data: UploadUrlResponseDto }
            ).data;

            // Upload both versions to S3 in parallel
            const [optimizedUpload, originalUpload] = await Promise.all([
              fetch(optimizedData.uploadUrl, {
                method: 'PUT',
                body: optimized,
                headers: { 'Content-Type': optimized.type },
              }),
              fetch(originalData.uploadUrl, {
                method: 'PUT',
                body: original,
                headers: { 'Content-Type': original.type },
              }),
            ]);

            if (optimizedUpload.ok && originalUpload.ok) {
              // Confirm upload with both URLs (optimized for display, original for backup)
              await api.upload.confirm({
                type: 'group',
                entityId: newGroupId,
                imageUrl: optimizedData.imageUrl,
                imageUrlOriginal: originalData.imageUrl,
              });
              // Update cache immediately with the new image
              queryClient.setQueryData(
                queryKeys.group(newGroupId),
                (old: GroupResponseDto | undefined) => {
                  if (old) {
                    return { ...old, image: optimizedData.imageUrl };
                  }
                  return old;
                }
              );
              // Also invalidate to ensure fresh data on next fetch
              queryClient.invalidateQueries({
                queryKey: queryKeys.group(newGroupId),
              });
            }
          } catch {
            toast.warning(t('groups:messages.imageUploadFailed'));
          }
        }

        // Wait a bit for the group to be fully committed before redirecting
        await new Promise(resolve => setTimeout(resolve, 500));

        toast.success(t('groups:messages.groupCreated'));
        router.push(`/groups/${newGroupId}`);
      } else if (mode === 'edit' && groupId) {
        // Handle budget for edit mode: distinguish between empty (null to clear) and zero (0)
        // If budget string is empty/undefined, send null to clear existing budget
        // If budget string has a value (including "0"), send the numeric value
        const budgetValue = budget?.trim() ? numericBudget : null; // Send null to explicitly clear the budget in edit mode

        // Build categorySettings for update - include if dates set, null to clear
        const updateCategorySettings =
          startDate || endDate
            ? {
                startDate: startDate || undefined,
                endDate: endDate || undefined,
              }
            : null; // Send null to clear existing dates

        // Build defaultSplitConfig based on method
        // Read from ref instead of state to ensure we have the latest values
        // (child component syncs via useEffect, which may not have updated state yet)
        const currentSplitConfig = splitConfigRef.current;
        let splitConfig: {
          percentages?: Record<string, number>;
          shares?: Record<string, number>;
        } | null = null;
        if (
          defaultSplitMethod === 'percentage' &&
          currentSplitConfig?.percentages &&
          Object.keys(currentSplitConfig.percentages).length > 0
        ) {
          splitConfig = { percentages: currentSplitConfig.percentages };
        } else if (
          defaultSplitMethod === 'shares' &&
          currentSplitConfig?.shares &&
          Object.keys(currentSplitConfig.shares).length > 0
        ) {
          splitConfig = { shares: currentSplitConfig.shares };
        }

        // Resolve the current user's effective permissions for field filtering.
        // editRole is already computed above but we re-derive here inside the
        // closure so TypeScript can narrow the type correctly.
        const editRole = group?.userRole ?? 'member';
        const editPerms = effectivePermissionsForRole(
          editRole,
          group?.memberPermissions
        );

        const updatePayload: UpdateGroupDto & {
          simplifyDebts?: boolean;
          defaultSplitMethod?: SplitMethod;
        } = {
          // Only include fields the user is permitted to edit.
          // Owners (editPerms all-true) always include everything.
          ...(editPerms.editName && {
            name: name.trim() || undefined,
          }),
          ...(editPerms.editDescription && {
            description: description.trim() ? description.trim() : null,
          }),
          ...(editPerms.editCategory && {
            category: category || undefined,
          }),
          ...(editPerms.editCurrency && {
            currency: currency ? currency : null,
          }),
          // Re-framing an existing photo persists the new focal point without
          // re-uploading the image — it is plain metadata on the group.
          ...(editPerms.editImage && {
            imageFocalY,
          }),
          ...(editPerms.editBudget && {
            budgetAmount: budgetValue,
          }),
          ...(editPerms.editDates && {
            categorySettings: updateCategorySettings,
          }),
          ...(editPerms.editDefaultSplit && {
            defaultSplitMethod,
            defaultSplitConfig: splitConfig,
          }),
          // memberPermissions is owner-only; the section only renders for owners
          // so this is safe. Backend enforces it as well.
          ...(editRole === 'owner' && { memberPermissions }),
        };

        // Single API call - backend handles everything including shares reset
        await updateGroupMutation.mutateAsync(updatePayload);

        toast.success(t('groups:messages.groupUpdated'));
        router.push(`/groups/${groupId}`);
      }
    },
    onError: (err: unknown) => {
      setError(handleError(err as Error));
    },
    validateFn: () => {
      if (!name.trim()) {
        return t('groups:errors.nameRequired');
      }
      // Validate percentages must equal 100% when using percentage method
      // Read from ref to ensure we have the latest values
      const currentConfig = splitConfigRef.current;
      if (defaultSplitMethod === 'percentage' && currentConfig?.percentages) {
        const total = Object.values(currentConfig.percentages).reduce(
          (sum, val) => sum + (val || 0),
          0
        );
        if (Math.abs(total - 100) > 0.01) {
          return t('groups:defaultSplit.percentageMustEqual100');
        }
      }
      return null;
    },
  });

  // Effect to populate form when group data is fetched (only on initial load)
  // Use queueMicrotask to batch state updates and satisfy lint rules
  useEffect(() => {
    if (mode === 'edit' && fetchedGroup && !hasInitialized) {
      const groupData = fetchedGroup as ExtendedGroupResponse;
      // Use queueMicrotask to defer state updates and avoid lint warning about
      // calling setState synchronously in effect (while being more predictable than setTimeout)
      queueMicrotask(() => {
        setGroup(groupData);
        setName(groupData.name);
        setDescription(groupData.description || '');
        setCategory(groupData.category as GroupCategory);
        setCurrency(groupData.currency || '');
        setImageFocalY(groupData.imageFocalY ?? 0.5);
        setDefaultSplitMethod(groupData.defaultSplitMethod || 'equal');
        setDefaultSplitConfig(groupData.defaultSplitConfig || {});
        setMemberPermissions(
          resolveMemberPermissions(groupData.memberPermissions)
        );
        setHasInitialized(true);
        setBudget(
          groupData.budgetAmount !== null &&
            groupData.budgetAmount !== undefined
            ? groupData.budgetAmount.toFixed(
                getCurrencyDecimals(groupData.currency || 'USD')
              )
            : ''
        );
        setNumericBudget(groupData.budgetAmount || 0);

        // Set category-specific settings
        if (groupData.categorySettings) {
          // Load startDate and endDate (used by all categories with different meanings)
          if (groupData.categorySettings.startDate) {
            const startDateStr =
              groupData.categorySettings.startDate.split('T')[0];
            setStartDate(startDateStr);
          }
          if (groupData.categorySettings.endDate) {
            const endDateStr = groupData.categorySettings.endDate.split('T')[0];
            setEndDate(endDateStr);
          }
        }
      });
    }
  }, [fetchedGroup, mode, hasInitialized, setDefaultSplitConfig]);

  // Reset initialization when group data is updated (e.g., after save and return)
  // This ensures the form picks up fresh data after mutations
  const lastUpdatedAt = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (mode === 'edit' && fetchedGroup) {
      const currentUpdatedAt = (fetchedGroup as ExtendedGroupResponse)
        .updatedAt;
      // If updatedAt changed, reset initialization to pick up new data
      if (lastUpdatedAt.current && currentUpdatedAt !== lastUpdatedAt.current) {
        queueMicrotask(() => setHasInitialized(false));
      }
      lastUpdatedAt.current = currentUpdatedAt;
    }
  }, [fetchedGroup, mode]);

  // Update expense count from expenses data
  useEffect(() => {
    if (expensesData) {
      const totalExpenses = expensesData.pages?.[0]?.expenses?.length || 0;
      // Wrap setState in setTimeout to avoid direct state setting in effect
      const timer = setTimeout(() => {
        setExpenseCount(prev => {
          if (prev !== totalExpenses) {
            return totalExpenses;
          }
          return prev;
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [expensesData]);

  // Set default currency for create mode
  useEffect(() => {
    if (mode === 'create' && user?.settings?.currency) {
      // Wrap setState in setTimeout to avoid direct state setting in effect
      const timer = setTimeout(() => {
        setCurrency(prev => {
          if (!prev && user.settings?.currency) {
            return user.settings.currency;
          }
          return prev;
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [mode, user?.settings?.currency]);

  // Manage loading state
  useEffect(() => {
    // Wrap setState in setTimeout to avoid direct state setting in effect
    const timer = setTimeout(() => {
      setLoading(prev => {
        if (mode === 'edit') {
          if (prev !== groupLoading) {
            return groupLoading;
          }
        } else if (
          mode === 'create' &&
          ready &&
          groupsLoaded &&
          commonLoaded &&
          currenciesLoaded
        ) {
          if (prev) {
            return false;
          }
        }
        return prev;
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [mode, groupLoading, ready, groupsLoaded, commonLoaded, currenciesLoaded]);

  // Groups with expenses can't be deleted from the web (a web-only
  // policy; the API only refuses outstanding balances). The example
  // group is the exception: "deleting" it just hides it on this device,
  // and it always ships with expenses — the guard would leave it stuck.
  const deleteBlockedByExpenses =
    expenseCount > 0 && groupId !== EXAMPLE_GROUP_ID;

  const handleDelete = async () => {
    if (!groupId || mode !== 'edit') return;

    if (deleteBlockedByExpenses) {
      setError(t('groups:messages.cannotDeleteWithExpenses'));
      return;
    }

    if (!confirm(t('groups:messages.confirmDelete', { name }))) {
      return;
    }

    setIsDeleting(true);
    setError('');

    // Call mutation first - onMutate will cancel queries and optimistically remove from cache
    // Then navigate in onSuccess to prevent race conditions
    deleteGroupMutation.mutate(groupId, {
      onSuccess: () => {
        toast.success(t('groups:messages.groupDeleted'));
        // Navigate after cache is updated
        router.push('/groups');
      },
      onError: (err: unknown) => {
        setIsDeleting(false);
        setError(handleError(err));
      },
    });
  };

  if (!user) {
    return null;
  }

  // Show skeleton while translations or data are loading
  // In edit mode, also wait for hasInitialized to ensure data is properly set
  if (!ready || loading || (mode === 'edit' && !hasInitialized)) {
    return (
      <AppLayout>
        <GroupFormSkeleton />
      </AppLayout>
    );
  }

  if (mode === 'edit' && !group && !fetchedGroup) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('groups:errors.groupNotFound')}
          </h3>
          <RouteChangeLink href="/groups">
            <Button variant="primary">
              {t('common:actions.back')} to {t('common:navigation.groups')}
            </Button>
          </RouteChangeLink>
        </div>
      </AppLayout>
    );
  }

  // Check permissions for edit mode: owners and admins always pass;
  // regular members may enter if they have at least one permitted field.
  if (
    mode === 'edit' &&
    group &&
    group.userRole !== 'admin' &&
    group.userRole !== 'owner' &&
    !hasAnyEditPermission(group.userRole ?? 'member', group.memberPermissions)
  ) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('groups:errors.onlyAdminsCanUpdate')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {t('groups:errors.notAuthorized')}
          </p>
          <RouteChangeLink href={`/groups/${groupId}`}>
            <Button variant="primary">
              {t('common:actions.back')} to {t('groups:group')}
            </Button>
          </RouteChangeLink>
        </div>
      </AppLayout>
    );
  }

  // Compute per-field permissions for the current user in edit mode.
  // In create mode the creator becomes the owner so everything is enabled.
  const perms = effectivePermissionsForRole(
    mode === 'edit' ? (group?.userRole ?? 'member') : 'owner',
    mode === 'edit' ? group?.memberPermissions : undefined
  );
  const lockedHint =
    mode === 'edit' && group?.userRole !== 'owner'
      ? t('groups:memberPermissions.lockedHint')
      : undefined;

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-8">
          <RouteChangeLink
            href={mode === 'edit' ? `/groups/${groupId}` : '/groups'}
            className="mr-4 p-2 rounded-lg hover:bg-[#FAF9F7] dark:hover:bg-gray-800/50 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </RouteChangeLink>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t(mode === 'edit' ? 'groups:editGroup' : 'groups:createGroup')}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t(
                mode === 'edit'
                  ? 'groups:editGroupSubtitle'
                  : 'groups:createGroupSubtitle'
              )}
            </p>
          </div>
        </div>

        {/* Error Alert */}
        <FormError error={error} />

        {mode === 'edit' && groupId && (
          <>
            <GroupProSettingsSection groupId={groupId} />
            <GroupRecurringSection groupId={groupId} />
          </>
        )}

        {/* Form */}
        <form
          onSubmit={e => handleFormSubmit(e, {})}
          className="space-y-6"
          data-testid="create-group-form"
        >
          {/* Basic Information */}
          <GroupBasicInfo
            name={name}
            setName={setName}
            description={description}
            setDescription={setDescription}
            category={category}
            setCategory={handleCategoryChange}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            isLoading={isLoading}
            isDeleting={isDeleting}
            groupId={groupId}
            currentAvatarUrl={group?.image}
            imageFocalY={imageFocalY}
            onImageFocalYChange={setImageFocalY}
            onAvatarUploaded={avatarUrl => {
              // Update the group state with new avatar
              if (group) {
                setGroup({ ...group, image: avatarUrl });
              }
              // Update cache immediately so the image persists across navigation
              if (groupId) {
                // First, update the cache directly with the new image
                queryClient.setQueryData(
                  queryKeys.group(groupId),
                  (old: GroupResponseDto | undefined) => {
                    if (old) {
                      return { ...old, image: avatarUrl };
                    }
                    return old;
                  }
                );
                // Also invalidate to ensure fresh data on next fetch
                queryClient.invalidateQueries({
                  queryKey: queryKeys.group(groupId),
                });
              }
            }}
            onAvatarDeleted={async () => {
              // Delete group image by setting it to null. A refused write
              // (offline, forbidden, the read-only example group) must be
              // reported here — an unhandled rejection escapes to the
              // async error boundary and blanks the page.
              try {
                await updateGroupMutation.mutateAsync({ image: null });
              } catch (error) {
                handleError(error);
                return;
              }
              // Update local state
              if (group) {
                setGroup({ ...group, image: undefined });
              }
              // Update cache
              if (groupId) {
                queryClient.setQueryData(
                  queryKeys.group(groupId),
                  (old: GroupResponseDto | undefined) => {
                    if (old) {
                      return { ...old, image: undefined };
                    }
                    return old;
                  }
                );
              }
              toast.success(t('groups:messages.imageDeleted'));
            }}
            onImageFileSelected={file => setPendingImageFile(file)}
            onImageFileCancelled={() => setPendingImageFile(null)}
            mode={mode}
            canEditName={perms.editName}
            canEditDescription={perms.editDescription}
            canEditCategory={perms.editCategory}
            canEditDates={perms.editDates}
            canEditImage={perms.editImage}
            lockedHint={lockedHint}
          />

          {/* Money Settings */}
          <GroupMoneySettings
            currency={currency}
            setCurrency={setCurrency}
            budget={budget}
            setBudget={setBudget}
            numericBudget={numericBudget}
            setNumericBudget={setNumericBudget}
            isLoading={isLoading}
            isDeleting={isDeleting}
            mode={mode}
            // Currency is immutable once the group has expenses (the backend
            // rejects the change to protect per-currency balances). A fresh
            // group can still be corrected.
            currencyLocked={mode === 'edit' && expenseCount > 0}
            canEditCurrency={perms.editCurrency}
            canEditBudget={perms.editBudget}
            lockedHint={lockedHint}
          />

          {/* Default Split Settings */}
          <GroupDefaultSplitSettings
            defaultSplitMethod={defaultSplitMethod}
            setDefaultSplitMethod={handleSplitMethodChange}
            isLoading={isLoading}
            isDeleting={isDeleting}
            groupId={groupId}
            defaultSplitConfig={defaultSplitConfig}
            setDefaultSplitConfig={setDefaultSplitConfig}
            hasParentInitialized={hasInitialized}
            canEditDefaultSplit={perms.editDefaultSplit}
            lockedHint={lockedHint}
          />

          {/* Payment Reminders - DISABLED: Backend not implemented yet. See docs/PAYMENT_REMINDERS_PLAN.md */}
          {/* <GroupPaymentReminders
            settlementReminderEnabled={settlementReminderEnabled}
            setSettlementReminderEnabled={setSettlementReminderEnabled}
            settlementReminderFrequency={settlementReminderFrequency}
            setSettlementReminderFrequency={setSettlementReminderFrequency}
            isLoading={isLoading}
            isDeleting={isDeleting}
          /> */}

          {/* Member Selection */}
          <GroupMemberSelection mode={mode} groupId={groupId} />

          {/* Member Permissions — the creator (owner-to-be) sets it on create;
              the owner manages it on edit. Hidden from non-owners editing. */}
          {(mode === 'create' ||
            (mode === 'edit' && group?.userRole === 'owner')) && (
            <GroupMemberPermissionsSection
              permissions={memberPermissions}
              onPermissionChange={(key, value) =>
                setMemberPermissions(prev => ({ ...prev, [key]: value }))
              }
              isLoading={isLoading}
              isDeleting={isDeleting}
            />
          )}

          {/* Form Actions - No box, page background */}
          <div
            className={`flex ${mode === 'edit' ? 'justify-between' : 'justify-end'} items-center pt-6`}
          >
            {mode === 'edit' && (
              <FormButton
                type="button"
                onClick={handleDelete}
                disabled={deleteBlockedByExpenses}
                loading={isDeleting}
                loadingText={t('common:actions.deleting')}
                variant="error"
                icon={TrashIcon}
                title={
                  deleteBlockedByExpenses
                    ? t('groups:messages.cannotDeleteWithExpenses')
                    : ''
                }
              >
                {t('groups:actions.deleteGroup')}
              </FormButton>
            )}

            <div className="flex items-center space-x-3">
              <RouteChangeLink
                href={mode === 'edit' ? `/groups/${groupId}` : '/groups'}
              >
                <Button
                  variant="outline"
                  data-testid="create-group-cancel-button"
                >
                  {t('common:actions.cancel')}
                </Button>
              </RouteChangeLink>
              <FormButton
                type="submit"
                disabled={!name.trim()}
                loading={isLoading}
                loadingText={t(
                  mode === 'edit'
                    ? 'common:actions.saving'
                    : 'common:actions.creating'
                )}
                variant="primary"
                icon={SaveIcon}
                data-testid="create-group-submit-button"
              >
                {t(
                  mode === 'edit' ? 'common:actions.save' : 'groups:createGroup'
                )}
              </FormButton>
            </div>
          </div>
        </form>

        {/* Expense Count Warning */}
        {mode === 'edit' && deleteBlockedByExpenses && (
          <Alert
            type="warning"
            message={
              <>
                <strong>{t('groups:messages.cannotDeleteTitle')}</strong>
                <br />
                {t('groups:messages.cannotDeleteDescription', {
                  count: expenseCount,
                })}
              </>
            }
            className="mt-6"
          />
        )}
      </div>
    </AppLayout>
  );
}

export default withAuth(GroupForm);
